import { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    from: '',
    to: '',
    date: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('from', formData.from);
      formBody.append('to', formData.to);
      formBody.append('date', formData.date);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d58l0q6isj01hmefqu5g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          from: '',
          to: '',
          date: '',
          message: ''
        });
        
        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setSubmitStatus('idle');
        }, 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="text-4xl font-bold text-white mb-6">Get Your Free Quote</h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Ready to move? Fill out the form and we'll get back to you with a competitive quote within the hour. No obligation, completely free.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-lg flex-shrink-0">
                  <i className="ri-phone-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">Call Us</h3>
                  <a href="tel:+447380118450" className="text-2xl font-bold text-blue-400 hover:text-blue-300 transition-colors cursor-pointer">
                    07380 118 450
                  </a>
                  <p className="text-sm text-gray-400 mt-1">Available 24/7 for your convenience</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-lg flex-shrink-0">
                  <i className="ri-mail-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">Email Us</h3>
                  <a href="mailto:info@manvanservices.org" className="text-2xl font-bold text-blue-400 hover:text-blue-300 transition-colors cursor-pointer">
                    info@manvanservices.org
                  </a>
                  <p className="text-sm text-gray-400 mt-1">We'll respond within 1 hour</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-lg flex-shrink-0">
                  <i className="ri-map-pin-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">Our Locations</h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-blue-400 font-semibold mb-1">London Office</p>
                      <p className="text-gray-300">
                        71-75 Shelton Street, Covent Garden<br />
                        London, WC2H 9JQ<br />
                        United Kingdom
                      </p>
                    </div>
                    <div>
                      <p className="text-blue-400 font-semibold mb-1">Essex Office</p>
                      <p className="text-gray-300">
                        Trafalgar House<br />
                        712 London Road<br />
                        West Thurrock<br />
                        RM20 3JT<br />
                        United Kingdom
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-700 rounded-2xl p-8 shadow-xl">
            <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-white mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-600 border border-gray-500 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-white mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-600 border border-gray-500 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                    placeholder="07380 118 450"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-600 border border-gray-500 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                  placeholder="john@example.com"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="from" className="block text-sm font-medium text-white mb-2">
                    Moving From *
                  </label>
                  <input
                    type="text"
                    id="from"
                    name="from"
                    value={formData.from}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-600 border border-gray-500 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                    placeholder="London"
                  />
                </div>
                <div>
                  <label htmlFor="to" className="block text-sm font-medium text-white mb-2">
                    Moving To *
                  </label>
                  <input
                    type="text"
                    id="to"
                    name="to"
                    value={formData.to}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-600 border border-gray-500 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400"
                    placeholder="Manchester"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="date" className="block text-sm font-medium text-white mb-2">
                  Preferred Moving Date *
                </label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-600 border border-gray-500 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white"
                />
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-medium text-white mb-2">
                  Additional Details
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={500}
                  className="w-full px-4 py-3 bg-gray-600 border border-gray-500 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-white placeholder-gray-400"
                  placeholder="Tell us about your move (e.g., number of rooms, special items, access issues)..."
                ></textarea>
                <p className="text-xs text-gray-400 mt-1">Maximum 500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 text-white py-4 rounded-lg hover:bg-blue-700 transition-all font-semibold text-lg shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? 'Sending...' : 'Get Free Quote'}
              </button>

              {submitStatus === 'success' && (
                <div className="mt-4 p-4 bg-green-900 border border-green-700 text-green-200 rounded-lg flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-xl flex-shrink-0 mt-0.5"></i>
                  <div>
                    <p className="font-semibold mb-1">Quote Request Sent Successfully!</p>
                    <p className="text-sm">We've received your information and will get back to you within the hour. Thank you for choosing our service!</p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-4 p-4 bg-red-900 border border-red-700 text-red-200 rounded-lg flex items-start gap-3">
                  <i className="ri-error-warning-fill text-xl flex-shrink-0 mt-0.5"></i>
                  <div>
                    <p className="font-semibold mb-1">Submission Failed</p>
                    <p className="text-sm">Something went wrong. Please call us directly at <a href="tel:+447380118450" className="underline font-semibold">07380 118 450</a> or email <a href="mailto:info@manvanservices.org" className="underline font-semibold">info@manvanservices.org</a>.</p>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
